import type { StaticImageData } from "next/image";
import type { ReactNode } from "react";
import Image from "next/image";

import { cn } from "../../../lib/utils";

export const DoomLayoutSection = ({
  bg,
  left,
  right,
  gradient = true,
  className = "",
  reverse = false,
  tight = false,
  darken = false,
  smooshRight = false,
  animateLeft = false,
  animateRight = true,
  moreRoom = false,
  blur,
}: {
  bg: StaticImageData | string;
  left: ReactNode;
  right: ReactNode;
  gradient?: boolean;
  className?: string;
  reverse?: boolean;
  tight?: boolean;
  darken?: boolean;
  smooshRight?: boolean;
  animateLeft?: boolean;
  animateRight?: boolean;
  moreRoom?: boolean;
  blur?: string;
}) => {
  return (
    <section className={`relative w-full ${className}`}>
      <div
        className={`absolute bottom-0 -z-[1] flex h-full w-full items-center duration-700 animate-in fade-in sm:top-0 sm:h-full 
        ${reverse ? "gradient-mask-t-50 sm:gradient-mask-tr-50" : "gradient-mask-t-50 sm:gradient-mask-tl-50"}
        `}
      >
        <div
          className={`absolute h-full w-full xl:w-4/5 
            ${reverse ? "left-0 gradient-mask-t-40 sm:gradient-mask-tr-60" : "right-0 gradient-mask-t-40 sm:gradient-mask-tl-60"} 
            ${smooshRight && "top-0 !w-4/5 !gradient-mask-l-40 md:!w-2/3"}`}
        >
          {/* bg */}
          <Image
            className={`object-cover object-[center_20%] brightness-90 lg:object-[100%_30%] 2xl:brightness-90
            ${reverse ? "gradient-mask-t-30 sm:gradient-mask-r-60" : "gradient-mask-t-30 sm:gradient-mask-l-60"} 
            ${(gradient || darken) && "!brightness-50"}`}
            alt={""}
            src={bg}
            placeholder="blur"
            blurDataURL={typeof bg === "string" ? (blur ?? bg) : undefined}
            fill={true}
          />
        </div>
      </div>

      {/* gradient */}
      {gradient && (
        <>
          <div
            className="absolute -z-[1] h-full w-full"
            style={{
              backgroundImage: `
        linear-gradient(240deg, rgba(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.1) 40%), 
        linear-gradient(0deg, rgba(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.1) 40%), 
        linear-gradient(120deg, rgba(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.1) 60%), 
        linear-gradient(180deg, rgba(2, 2, 2, 0.5) 0%, rgba(2, 2, 2, 0.1) 30%),
        linear-gradient(to top, rgba(2, 2, 2, 0.6) 0%, rgba(2, 2, 2, 0.1) 50%)`,
            }}
          />
          <div className="absolute -z-[1] h-full w-full gradient-mask-b-70 [background-image:radial-gradient(circle,_rgba(0,0,0,0.5)_40%,_rgba(0,0,0,0)_60%)]" />
        </>
      )}

      <div
        className={`container mx-auto flex h-full w-full items-center sm:py-12 lg:py-12 ${reverse && "justify-end"} ${!tight && "px-6 lg:px-12"}`}
      >
        <div
          className={cn(
            "flex w-full flex-col items-center gap-4 sm:flex-row sm:justify-between",
            reverse ? "flex-col-reverse sm:flex-row" : "flex-col sm:flex-row",
          )}
        >
          <div
            className={cn(
              "w-full sm:w-[50%] md:w-[20rem] lg:w-[24rem] xl:w-[26rem]",
              animateLeft && "transition duration-300 hover:scale-[1.05]",
              moreRoom && "lg:!w-[34rem]",
            )}
          >
            {left}
          </div>
          <div
            className={cn(
              "w-full transition-all duration-300 sm:w-[50%] md:ml-4 md:w-[24rem] lg:-mt-4 lg:ml-8 lg:w-[24rem] xl:w-[32rem]",
              animateRight && "hover:scale-[1.05]",
            )}
          >
            {right}
          </div>
        </div>
      </div>
    </section>
  );
};
